// src/pages/ResearchPageWrapper.jsx
// Wrapper für EIN Research-Projekt (/main-research/:researchProjectId):
// Auth-/Permission-Gate, Projekt-Metadaten laden, Zurück-Button zur Übersicht,
// Fullscreen-Toggle und drei Reiter (Q&A · Zitate · Case Studies). Der dritte
// Reiter ist pro Projekt umbenennbar (Doppelklick; Sakralbauten: "Kirchen").
// Hält EINE useResearchEditor-Instanz, die beide Eintragstypen verwaltet,
// damit Querverweise reiterübergreifend funktionieren.

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { canAccess, canEdit } from "../utils/permissions";
import { useResearchEditor } from "../services/research/useResearchEditor";
import {
  getAllResearchProjects,
  updateResearchProject,
  setLastResearchProjectId,
  DEFAULT_CASE_TAB_LABEL,
} from "../services/azureResearchProjectsService";
import ResearchTool from "../components/research/ResearchTool";

const TABS_BASE = [
  { id: "qna", label: "Q&A" },
  { id: "zitate", label: "Zitate" },
];
// Dritter Reiter: Label kommt pro Projekt aus caseTabLabel (leer = Standard).
const CASES_TAB_ID = "cases";

const ResearchPageWrapper = () => {
  const { researchProjectId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState("qna");
  const [scrollToId, setScrollToId] = useState(null);

  // Projekt-Metadaten: alle Projekte laden, damit neben dem aktuellen Projekt
  // auch die Datei-Präfixe der ANDEREN Projekte bekannt sind (Ausblendung im
  // Wurzel-Ordner des Alt-Projekts, siehe azureResearchFilesService).
  const [projects, setProjects] = useState(null); // null = lädt noch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getAllResearchProjects();
      if (!cancelled) setProjects(data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const project = useMemo(
    () => (projects || []).find((p) => p.id === researchProjectId) || null,
    [projects, researchProjectId]
  );

  // Editor-Kontext des Projekts (stabil per Projekt-ID).
  const editorProject = useMemo(() => {
    if (!project) return null;
    const excludeFilePrefixes = (projects || [])
      .filter((p) => p.id !== project.id && p.filesPrefix)
      .map((p) => p.filesPrefix);
    return {
      id: project.id,
      sourceTable: project.sourceTable,
      filesPrefix: project.filesPrefix,
      excludeFilePrefixes,
    };
  }, [project, projects]);

  // Letztgeöffnetes Projekt merken (für Auto-Open beim Klick auf "Research").
  useEffect(() => {
    if (project) setLastResearchProjectId(project.id);
  }, [project]);

  const editor = useResearchEditor(user, editorProject);
  const readOnly = !canEdit(user, "research");

  const { setEditingId } = editor;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // --- Dritter Reiter: projektspezifisches Label + Inline-Umbenennen ---------
  const caseTabLabel = project?.caseTabLabel || DEFAULT_CASE_TAB_LABEL;
  const [renamingCaseTab, setRenamingCaseTab] = useState(false);
  const [caseTabDraft, setCaseTabDraft] = useState("");

  const startCaseTabRename = useCallback(() => {
    if (readOnly) return;
    setCaseTabDraft(caseTabLabel);
    setRenamingCaseTab(true);
  }, [readOnly, caseTabLabel]);

  const cancelCaseTabRename = useCallback(() => setRenamingCaseTab(false), []);

  const saveCaseTabRename = useCallback(async () => {
    setRenamingCaseTab(false);
    if (!project) return;
    const trimmed = caseTabDraft.trim();
    if (trimmed === caseTabLabel) return;
    // Leer oder Standardtext -> Feld leeren, damit der Standard wieder greift.
    const next = !trimmed || trimmed === DEFAULT_CASE_TAB_LABEL ? "" : trimmed;
    try {
      await updateResearchProject(project.id, { caseTabLabel: next });
      setProjects((prev) =>
        (prev || []).map((p) => (p.id === project.id ? { ...p, caseTabLabel: next } : p))
      );
    } catch (err) {
      console.error("Reiter umbenennen fehlgeschlagen:", err);
    }
  }, [project, caseTabDraft, caseTabLabel]);

  const onScrolled = useCallback(() => setScrollToId(null), []);

  const goToOverview = useCallback(() => {
    // state.showList unterdrückt das Auto-Open in der Übersicht.
    navigate("/main-research", { state: { showList: true } });
  }, [navigate]);

  // Zu einem Eintrag springen – ggf. Reiter wechseln (heterogene Querverweise).
  const jumpToEntity = useCallback(
    ({ kind, id }) => {
      const tab = kind === "quote" ? "zitate" : "qna";
      if (tab !== activeTabRef.current) setEditingId(null);
      setActiveTab(tab);
      setScrollToId(id);
    },
    [setEditingId]
  );

  const handleTab = useCallback(
    (tab) => {
      if (tab === activeTabRef.current) return;
      setEditingId(null);
      setActiveTab(tab);
      setScrollToId(null);
    },
    [setEditingId]
  );

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const next = !prev;
      const contentArea = document.querySelector(".content-area");
      if (contentArea) contentArea.classList.toggle("fullscreen", next);
      return next;
    });
  }, []);

  // Fullscreen-Klasse beim Unmount aufräumen.
  useEffect(() => {
    return () => {
      const contentArea = document.querySelector(".content-area");
      if (contentArea) contentArea.classList.remove("fullscreen");
    };
  }, []);

  if (authLoading) {
    return <div className="loading">Wird geladen…</div>;
  }

  if (!user || !canAccess(user, "research")) {
    return (
      <div className="access-denied">
        <h3>Zugriff verweigert</h3>
        <p>Sie haben keine Berechtigung für diesen Bereich.</p>
      </div>
    );
  }

  if (projects === null) {
    return <div className="loading">Research-Projekt wird geladen…</div>;
  }

  if (!project) {
    return (
      <div className="research-page">
        <div className="research-not-found">
          <h3>Research-Projekt nicht gefunden</h3>
          <p>Das Projekt existiert nicht mehr oder wurde gelöscht.</p>
          <button type="button" className="btn btn-secondary" onClick={goToOverview}>
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="research-page">
      <div className="research-tabs" role="tablist">
        <button
          type="button"
          className="research-back-btn"
          onClick={goToOverview}
          title="Zurück zur Projektübersicht"
        >
          &#x2039; Übersicht
        </button>
        <span className="research-project-name" title={project.description || project.name}>
          {project.name}
        </span>
        {TABS_BASE.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`research-tab${activeTab === t.id ? " active" : ""}`}
            onClick={() => handleTab(t.id)}
          >
            {t.label}
          </button>
        ))}
        {renamingCaseTab ? (
          <input
            type="text"
            className="research-tab-rename-input"
            value={caseTabDraft}
            autoFocus
            onFocus={(e) => e.target.select()}
            onChange={(e) => setCaseTabDraft(e.target.value)}
            onBlur={saveCaseTabRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveCaseTabRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelCaseTabRename();
              }
            }}
          />
        ) : (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === CASES_TAB_ID}
            className={`research-tab${activeTab === CASES_TAB_ID ? " active" : ""}`}
            onClick={() => handleTab(CASES_TAB_ID)}
            onDoubleClick={startCaseTabRename}
            title={readOnly ? caseTabLabel : "Doppelklick zum Umbenennen des Reiters"}
          >
            {caseTabLabel}
          </button>
        )}
      </div>

      {activeTab === "qna" && (
        <ResearchTool
          editor={editor}
          kind="question"
          items={editor.questions}
          readOnly={readOnly}
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
          scrollToId={scrollToId}
          onScrolled={onScrolled}
          onJumpEntity={jumpToEntity}
          projectName={project.name}
        />
      )}

      {activeTab === "zitate" && (
        <ResearchTool
          editor={editor}
          kind="quote"
          items={editor.quotes}
          readOnly={readOnly}
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
          scrollToId={scrollToId}
          onScrolled={onScrolled}
          onJumpEntity={jumpToEntity}
        />
      )}

      {activeTab === CASES_TAB_ID && (
        <div className="research-cases">
          <h2 className="research-title">{caseTabLabel}</h2>
          <p className="qna-empty">Dieser Bereich ist noch in Vorbereitung.</p>
        </div>
      )}
    </div>
  );
};

export default ResearchPageWrapper;
